import { useState, useCallback, useRef } from 'react';

export function useAccordion() {
  const [expandedRows, setExpandedRows] = useState({});
  const [childData, setChildData] = useState({});
  const [loadingRows, setLoadingRows] = useState({});
  const abortControllers = useRef({});

  // Use refs for stable toggleRow callback
  const expandedRowsRef = useRef(expandedRows);
  expandedRowsRef.current = expandedRows;
  const childDataRef = useRef(childData);
  childDataRef.current = childData;

  const toggleRow = useCallback(async (rowId, { fetchChildren, level = 1 } = {}) => {
    const isCurrentlyExpanded = !!expandedRowsRef.current[rowId];

    setExpandedRows((prev) => {
      if (prev[rowId]) {
        const next = { ...prev };
        delete next[rowId];
        Object.keys(next).forEach((key) => {
          if (key.startsWith(rowId + ':')) delete next[key];
        });
        return next;
      }
      return { ...prev, [rowId]: level };
    });

    if (isCurrentlyExpanded) return;
    if (childDataRef.current[rowId]) return;

    if (fetchChildren) {
      if (abortControllers.current[rowId]) {
        abortControllers.current[rowId].abort();
      }
      const controller = new AbortController();
      abortControllers.current[rowId] = controller;

      setLoadingRows((prev) => ({ ...prev, [rowId]: true }));
      try {
        const data = await fetchChildren(controller.signal);
        if (!controller.signal.aborted) {
          setChildData((prev) => ({ ...prev, [rowId]: data }));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch children:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingRows((prev) => ({ ...prev, [rowId]: false }));
        }
        delete abortControllers.current[rowId];
      }
    }
  }, []); // stable reference — reads state via refs

  const isExpanded = useCallback((rowId) => !!expandedRows[rowId], [expandedRows]);
  const isLoading = useCallback((rowId) => !!loadingRows[rowId], [loadingRows]);
  const getChildren = useCallback((rowId) => childData[rowId] || [], [childData]);

  const reset = useCallback(() => {
    Object.values(abortControllers.current).forEach((c) => c.abort());
    abortControllers.current = {};
    setExpandedRows({});
    setChildData({});
    setLoadingRows({});
  }, []);

  return {
    toggleRow, isExpanded, isLoading, getChildren, reset, expandedRows,
  };
}
